import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router';
import { qk } from '@immoaktas/api-client';
import { api } from '../lib/api';
import { ErrorState, Skeleton } from '../ui';
import { handoverPath, legacyTenancyTarget } from './scope';

/** Old route → new route, query string kept (/posteingang → /mitteilungen). */
export function RedirectKeepQuery({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/** Server notification links "/nachrichten?tenancyId=…" open the messages tab of the tenancy. */
export function MessagesRedirect() {
  const [sp] = useSearchParams();
  const id = sp.get('tenancyId');
  return <Navigate to={id ? `/mietverhaeltnisse/${encodeURIComponent(id)}/nachrichten` : '/mitteilungen?ansicht=mieter'} replace />;
}

/**
 * Old tenancy links (/mietverhaeltnisse/:id[/:sub][?tab=…], notification mails, rows that only know the tenancy):
 * once the tenancy is loaded they are replaced by the nested route inside its unit (level 3). `replace` keeps the
 * browser history clean: back goes to the page the link was clicked on, not to the redirect.
 */
export function LegacyTenancyRedirect() {
  const { id = '', sub } = useParams();
  const { search } = useLocation();
  const q = useQuery({ queryKey: qk.tenancy(id), queryFn: () => api.tenancies.get(id) });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton count={6} h={24} />;
  return <Navigate to={legacyTenancyTarget(q.data.unitId, id, sub, search)} replace />;
}

/**
 * Tenancy pages inside a unit (/einheiten/:unitId/mietverhaeltnisse/:id/…): a link with the wrong unit (unit changed,
 * hand-typed URL) is replaced by the tenancy's own unit, so the trail and the way back are always right.
 */
export function TenancyInUnit({ children }: { children: ReactNode }) {
  const { unitId = '', id = '' } = useParams();
  const { pathname, search } = useLocation();
  const q = useQuery({ queryKey: qk.tenancy(id), queryFn: () => api.tenancies.get(id) });
  // old "?tab=konto" style links on the overview route become the tab's own route
  if (new URLSearchParams(search).has('tab') && pathname.replace(/\/+$/, '').endsWith(`/mietverhaeltnisse/${id}`))
    return <Navigate to={legacyTenancyTarget(unitId, id, undefined, search)} replace />;
  if (q.data && q.data.unitId !== unitId)
    return <Navigate to={`${pathname.replace(`/einheiten/${unitId}/`, `/einheiten/${q.data.unitId}/`)}${search}`} replace />;
  return <>{children}</>;
}

/** /mietverhaeltnisse/:id/uebergabe/:handoverId (deadlines, notifications): the protocol inside the tenancy's unit. */
export function LegacyHandoverRedirect() {
  const { id = '', handoverId = '' } = useParams();
  const q = useQuery({ queryKey: qk.tenancy(id), queryFn: () => api.tenancies.get(id) });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton count={6} h={24} />;
  return <Navigate to={handoverPath({ id, unitId: q.data.unitId }, handoverId)} replace />;
}
